import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Qué es la estimulación theta burst acelerada?",
    answer:
      "La estimulación theta burst es un patrón de pulsos de TMS que imita los ritmos naturales del cerebro. En SAINT® se administra de forma acelerada: 50 sesiones durante cinco días, en lugar de una sesión diaria durante varias semanas.",
  },
  {
    question: "¿Está autorizado por la FDA?",
    answer:
      "SAINT® está autorizado por la FDA (510(k) K220177, septiembre de 2022) para el trastorno depresivo mayor en adultos que no han mejorado con antidepresivos. La elegibilidad se confirma mediante una evaluación psiquiátrica.",
  },
];

export const esAcceleratedThetaBurst = makeEsArticle({
  slug: "accelerated-tms-theta-burst-stimulation",
  title: "TMS acelerada con estimulación theta burst: explicación",
  description:
    "La estimulación theta burst acelerada es el patrón de pulsos que utiliza SAINT® TMS: 50 sesiones en cinco días para el trastorno depresivo mayor en adultos que no han mejorado con antidepresivos.",
  h1: "TMS acelerada con estimulación theta burst",
  breadcrumbLabel: "TMS acelerada theta burst",
  shortAnswer:
    "La estimulación theta burst acelerada es un patrón de TMS que administra pulsos en ráfagas cortas que imitan los ritmos naturales del cerebro. SAINT® combina este patrón con orientación individualizada por fMRI y un calendario acelerado de 50 sesiones durante cinco días. Está autorizado por la FDA para el trastorno depresivo mayor en adultos que no han mejorado con antidepresivos. La elegibilidad se confirma mediante una evaluación psiquiátrica.",
  keyTakeaways: [
    "La estimulación theta burst administra pulsos en ráfagas cortas.",
    "SAINT® combina theta burst acelerada con orientación por fMRI.",
    "Se administran 50 sesiones durante cinco días consecutivos.",
    "Está autorizado por la FDA para el trastorno depresivo mayor en adultos.",
    "La elegibilidad se confirma mediante una evaluación psiquiátrica.",
  ],
  sections: [
    {
      heading: "Qué es la estimulación theta burst",
      paragraphs: [
        "La estimulación theta burst es un patrón de estimulación magnética transcraneal que administra pulsos en ráfagas cortas a alta frecuencia, imitando los ritmos theta naturales del cerebro. En SAINT®, este patrón se administra de forma acelerada: 50 sesiones durante cinco días consecutivos, con aproximadamente 10 sesiones por día.",
      ],
    },
    {
      heading: "Cómo se combina con fMRI en SAINT®",
      paragraphs: [
        "SAINT® combina la estimulación theta burst acelerada con orientación individualizada por fMRI para localizar el circuito cerebral asociado con la depresión en cada persona. Esta orientación personalizada distingue a SAINT® de la TMS convencional, que normalmente utiliza mediciones del cuero cabelludo.",
        "SAINT® está autorizado por la FDA (510(k) K220177, septiembre de 2022) para el trastorno depresivo mayor en adultos que no han mejorado con antidepresivos. No está indicado para ansiedad, trastorno bipolar, TEPT u otras condiciones.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
